package com.swetdot.swetgram.domain

import kotlin.time.Instant

/*
 * Client domain models for the profile, privacy, blocking and presence surface.
 *
 * Same rule as `Models.kt`: nothing here may import from `com.swetdot.swetgram.protocol`
 * (IMPLEMENTATION_NOTES §10). The wire enums each carry an `UNKNOWN` member for forward
 * compatibility; the ones below deliberately do NOT, except where the value genuinely comes back
 * from the server and has to survive a version the client has never heard of. An "unknown"
 * privacy audience is not a state the UI can render or the client can act on, so the mapper is
 * where it has to be resolved -- fail closed, once, rather than at every call site.
 */

// ----------------------------------------------------------------------------- privacy

/**
 * Who may see one of the two presence fields.
 *
 * [SHARED_GROUPS] is the middle setting: people who share a group with the user. It is not
 * "contacts" -- this app has no contact list.
 */
enum class PresenceAudience { EVERYONE, SHARED_GROUPS, NOBODY }

/**
 * Who may open a new direct conversation with the user.
 *
 * Note the third member is [EXISTING_CHATS], not "nobody": the contract has no `nobody` here on
 * purpose, because the setting gates conversation CREATION rather than delivery. Someone the
 * user is already talking to keeps being able to write, whatever this is set to -- so
 * [EXISTING_CHATS] is the strictest reachable choice, and a UI must not offer a fourth.
 */
enum class MessageAudience { EVERYONE, SHARED_GROUPS, EXISTING_CHATS }

/**
 * The whole of `GET /users/me/privacy`.
 *
 * All six are always present. The contract's update schema is the identical object, so an edit
 * is a full replacement rather than a patch -- see [com.swetdot.swetgram.data.profile.PrivacyRepository.update].
 */
data class PrivacySettings(
    val showOnline: PresenceAudience,
    val showLastSeen: PresenceAudience,
    val showDateOfBirth: PresenceAudience,
    val showLocation: PresenceAudience,
    val allowMessagesFrom: MessageAudience,
    /** False removes `profileUrl` from profiles. */
    val allowProfileLink: Boolean,
)

/**
 * What the client tells the server about itself. DECISIONS §4: foreground is online, background
 * or a clean shutdown is offline.
 *
 * There is no third member because presence only ever travels outward -- the client declares it
 * and never decodes it, so there is no unrecognised value to absorb.
 */
enum class PresenceState { ONLINE, OFFLINE }

// ---------------------------------------------------------------------------- accounts

/** Response-only, so an [UNKNOWN] is reachable: a role added later must not break the screen. */
enum class AccountRole { USER, MODERATOR, ADMIN, UNKNOWN }

/**
 * The signed-in user's own account -- everything `UserPublic` has, plus what only the owner sees.
 *
 * [user] is the same [User] every other screen renders, so an avatar or a name is drawn by the
 * same code here as in a chat header.
 */
data class MyProfile(
    val user: User,
    /** DECISIONS §4: "Email is never public." It exists on this type and on no other. */
    val email: String,
    val isEmailVerified: Boolean,
    val role: AccountRole,
    /**
     * SPS §13.1's and SDS §15.1's JOINED DATE, `UserPublic.created_at`.
     *
     * It lives HERE and not on [User], which is where it belongs on paper -- the contract marks
     * `created_at` required on every `UserPublic`, so every rendered user has one. Putting it on
     * [User] means a column on the cached user row, a migration, and a change to every mapper and
     * every test that builds a [User]; this profile-only type reaches the one screen that shows
     * it without any of that. Recorded as a known gap: a public profile therefore still cannot
     * show a joined date, because `UserProfile.user` is the shared [User].
     *
     * Nullable only as a parse fallback, exactly like [usernameChangeAvailableAt]: an unreadable
     * date drops one line rather than failing the screen, and "Joined" with nothing after it is
     * worse than no line at all -- so the caller omits it entirely.
     */
    val joinedAt: Instant?,
    /**
     * The optimistic-concurrency token for the next edit. Nullable because the contract marks it
     * optional; [startEdit] refuses to invent one, since a guessed version would be checked
     * against some other revision and could silently drop the loser of two concurrent edits.
     */
    val version: Int?,
    /** When the username may next be changed. Null means it may be changed now. */
    val usernameChangeAvailableAt: Instant?,
) {
    fun canChangeUsername(now: Instant): Boolean =
        usernameChangeAvailableAt == null || usernameChangeAvailableAt <= now

    /**
     * A blank edit pinned to the revision this profile was read at, or null when the server sent
     * no version -- in which case the correct move is to re-fetch, not to save.
     */
    fun startEdit(): ProfileEdit? = version?.let { ProfileEdit(version = it) }
}

/** `full` or `limited`; a blocked viewer sees the limited form. There is no third state. */
enum class ProfileVisibility { FULL, LIMITED }

/**
 * Somebody else's profile, as this viewer is allowed to see it.
 *
 * Presence lives here rather than being read off [user] because [User.isOnline] is a non-null
 * Boolean: it collapses "hidden" into `false`, which would render a user who hid their presence
 * as flatly offline. The two fields below keep the distinction the server actually sends.
 */
data class UserProfile(
    val user: User,
    val visibility: ProfileVisibility,
    /** True when the VIEWER has blocked this user. It never reveals the reverse direction. */
    val isBlockedByMe: Boolean,
    /** Absent when the user turned profile links off, so a share action must be hidden. */
    val profileUrl: String?,
    /** Null means hidden by a privacy setting or a block -- NOT offline. */
    val isOnline: Boolean?,
    /**
     * Null means hidden or never recorded. The two are indistinguishable on the wire and render
     * identically (nothing is shown), so the ambiguity costs nothing.
     */
    val lastSeenAt: Instant?,
    val media: List<ProfileMedia> = emptyList(),
    val links: List<ProfileLink> = emptyList(),
    val music: List<ProfileMedia> = emptyList(),
    val voice: List<ProfileMedia> = emptyList(),
) {
    /** Show neither a dot nor a last-seen line when this is true. */
    val isPresenceHidden: Boolean get() = isOnline == null && lastSeenAt == null
}

// ------------------------------------------------------------------------- profile edit

/**
 * One field of a profile edit.
 *
 * [Clear] exists because the contract distinguishes an absent key ("leave alone") from an
 * explicit JSON null ("remove this"), and a plain nullable Kotlin field cannot express both --
 * `ProtocolJson` omits nulls, so a null would always mean "leave alone" and a bio could never be
 * emptied. Only [bio][ProfileEdit.bio] and [avatarMediaId][ProfileEdit.avatarMediaId] are
 * nullable in the contract, so only they take this type.
 */
sealed interface FieldEdit<out T> {
    data object Unchanged : FieldEdit<Nothing>
    data object Clear : FieldEdit<Nothing>
    data class Set<T>(val value: T) : FieldEdit<T>
}

/**
 * A pending edit to the signed-in user's own profile.
 *
 * [version] is required and has no default: it is what the server checks to answer
 * `409 version_conflict`, and a defaulted one would let a stale write pass. Build one with
 * [MyProfile.startEdit] so it is always pinned to a revision that was actually read.
 */
data class ProfileEdit(
    val version: Int,
    val displayName: String? = null,
    val username: String? = null,
    val bio: FieldEdit<String> = FieldEdit.Unchanged,
    val dateOfBirth: FieldEdit<String> = FieldEdit.Unchanged,
    val showBirthYear: FieldEdit<Boolean> = FieldEdit.Unchanged,
    val location: FieldEdit<String> = FieldEdit.Unchanged,
    val useLocationHint: Boolean = false,
    val avatarMediaId: FieldEdit<String> = FieldEdit.Unchanged,
) {
    /**
     * The contract requires at least one field beside the version, so an edit that changes
     * nothing is a guaranteed 400 and is rejected before it costs a round trip.
     */
    val hasChanges: Boolean
        get() = displayName != null ||
            username != null ||
            bio != FieldEdit.Unchanged ||
            dateOfBirth != FieldEdit.Unchanged ||
            showBirthYear != FieldEdit.Unchanged ||
            location != FieldEdit.Unchanged ||
            useLocationHint ||
            avatarMediaId != FieldEdit.Unchanged
}

// ----------------------------------------------------------------------------- blocking

/**
 * An entry of the blocked list.
 *
 * [blockedAt] is nullable only as a parse fallback: a row with an unreadable date still has to be
 * shown, because it is the only place the user can unblock that account again.
 */
data class BlockedUser(
    val user: User,
    val blockedAt: Instant?,
)

// ----------------------------------------------------------------------------------- QR

/** Response-only, so an unrecognised target degrades instead of failing the screen. */
enum class QrTarget { USER, PUBLIC_GROUP, PRIVATE_GROUP_INVITE, UNKNOWN }

/**
 * A profile or group QR code.
 *
 * [payload] is built on the immutable target id rather than a username (DECISIONS §3): a printed
 * code outlives the name it was made from, and usernames are released for reuse 30 days after a
 * change. For [QrTarget.PRIVATE_GROUP_INVITE] the payload embeds an invite token, so the image
 * itself is a credential -- anyone who can see it can join.
 */
data class ProfileQr(
    val payload: String,
    val imageUrl: String,
    val target: QrTarget,
    val targetId: String,
)

// ---------------------------------------------------------------------- account deletion

/**
 * A scheduled account deletion, cancellable until [scheduledFor].
 *
 * [scheduledFor] is nullable for one reason: if the server ever sent a date this client could not
 * parse, showing the epoch would tell someone their account was deleted in 1970 on the one screen
 * where a wrong answer is unrecoverable. Null means "scheduled, date unknown -- re-fetch".
 */
data class ScheduledDeletion(
    val scheduledFor: Instant?,
    /** The contract fixes this at 30, but it is displayed as sent rather than assumed. */
    val recoveryDays: Int,
)
